// frontend/src/components/header/Header.jsx

import "./header.css";
import React, { useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Context } from "../../context/Context";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faCircleXmark,
  faSearch,
  faTimes,
  faBell,
} from "@fortawesome/free-solid-svg-icons";
import { toHuman } from "../../utils/stages";

export default function Header() {
  const { user, dispatch } = useContext(Context);
  const [searchOpen, setSearchOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]); // last 10
  const [showNotifications, setShowNotifications] = useState(false);

  const navigate = useNavigate();
  const menuRef = useRef(null);
  const notifRef = useRef(null);
  const lastCheckedRef = useRef(null);

  const handleLogout = () => {
    dispatch({ type: "LOGOUT" });
    navigate("/");
  };

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleLinkClick = () => {
    setIsMenuOpen(false);
  };

  const handleClickOutside = (e) => {
    if (menuRef.current && !menuRef.current.contains(e.target)) {
      setIsMenuOpen(false);
    }

    if (notifRef.current && !notifRef.current.contains(e.target)) {
      setShowNotifications(false);
    }
  };

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "auto";
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen]);

  // Fetch unread notifications for the logged-in user (with polling)
  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      setNotifications([]);
      lastCheckedRef.current = null;    // Reset when logged out / no user
      return;
    }

    let cancelled = false;

    const fetchUnreadNotifications = async (isInitial = false) => {
      try {
        // Build URL with optional ?since=
        let url = "/notifications";
        if (!isInitial && lastCheckedRef.current) {
          const params = new URLSearchParams({
            since: lastCheckedRef.current,
          }).toString();
          url = `/notifications?${params}`;
        }

        const res = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        });

        if (cancelled) return;

        const list = Array.isArray(res.data) ? res.data : [];

        if (isInitial) {
          // First load: replace with full unread list
          setUnreadCount(list.length);
          setNotifications(list.slice(0, 10));
        } else if (list.length > 0) {
          // Polling: only new notifications are returned
          setUnreadCount((prev) => prev + list.length);
          setNotifications((prev) =>
            [...list, ...prev].slice(0, 10) // prepend new, keep last 10
          );
        }

        // Update "last checked" based on newest notification from the server
        if (list.length > 0 && list[0].createdAt) {
          lastCheckedRef.current = list[0].createdAt;
        } else if (!lastCheckedRef.current) {
          // Fallback for very first request if something is weird
          lastCheckedRef.current = new Date().toISOString();
        }
      } catch (err) {
        console.error("Failed to load notifications:", err);
      }
    };

    // Initial fetch (no ?since=)
    fetchUnreadNotifications(true);

    // Poll every 15 seconds
    const intervalId = setInterval(() => {
      fetchUnreadNotifications(false);
    }, 15000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [user]);

  async function dismissNotification(id) {
    try {
      await axios.patch(
        `/notifications/${id}/read`,
        {},
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      setNotifications((prev) => prev.filter((n) => n._id !== id));
      setUnreadCount((prev) => Math.max(prev - 1, 0));
    } catch (err) {
      console.error("Error dismissing notification:", err);
    }
  }

  async function openNotification(n) {
    try {
      // 1) Mark as read
      await axios.patch(
        `/notifications/${n._id}/read`,
        {},
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      // 2) Optimistically update local state
      setNotifications((prev) => prev.filter((x) => x._id !== n._id));
      setUnreadCount((prev) => Math.max(prev - 1, 0));

      // 3) Navigate to the draft
      if (n.draftId) {
        if (n.type === "COMMENT_MENTION" && n.commentId) {
          // Deep link to the comment
          const params = new URLSearchParams({
            postId: n.draftId,
            commentId: n.commentId,
            commentType: n.commentType || "COMMENT",
          });

          navigate(`/write?${params.toString()}`);
        } else {
          // Default behavior for stage-change / published
          navigate(`/write?postId=${n.draftId}`);
        }
      }

      // 4) Close the dropdown
      setShowNotifications(false);
    } catch (err) {
      console.error("Error opening notification:", err);
    }
  }

  const renderNotificationBody = (n) => {
    if (n.type === "STAGE_CHANGE") {
      return (
        <>
          Moved to{" "}
          <span className="notif-stage-bold">{toHuman(n.toStage)}</span>
        </>
      );
    }

    if (n.type === "PUBLISHED") {
      return (
        <>
          <span className="notif-stage-bold">Published</span>
        </>
      );
    }

    if (n.type === "COMMENT_MENTION") {
      const who = n.actorName || "Someone";
      const isInline = n.commentType === "INLINE";

      if (isInline) {
        return (
          <>
            <span className="notif-stage-bold">{who}</span> mentioned you in an inline comment
          </>
        );
      }

      return (
        <>
          <span className="notif-stage-bold">{who}</span> mentioned you in a comment
        </>
      );
    }

    // Fallback / future types
    return <>New activity on this draft</>;
  };

  return (
    <div className="header-smaller-screen">
      <div className="logoHome">
        <Link to="/">
          <img
            src={
              process.env.REACT_APP_AWS_S3_IMAGE_FRONTEND_URL +
              "/Logo_FLOW_New_Cropped_800x800.png"
            }
            alt="Logo"
          />
        </Link>
      </div>
      <div className="header-right-Home">
        {user ? (
          <>
            {user.isAuthor ? (
              <>
                <div className="sign-in" onClick={handleLogout}>
                  <a
                    href="#"
                    className="link-style-header"
                    onClick={handleLogout}
                  >
                    LOGOUT
                  </a>
                </div>
              </>
            ) : (
              <div className="sign-in" onClick={handleLogout}>
                <a href="#" className="link-style-header">
                  LOGOUT
                </a>
              </div>
            )}
          </>
        ) : (
          <div className="sign-in">
            <Link
              to="/login"
              className="link-style-header"
              onClick={handleLinkClick}
            >
              SIGN IN
            </Link>
          </div>
        )}
        <Link to="/subscribe">
          <button
            className="subscribe-btn-Home buttonHome"
            onClick={handleLinkClick}
          >
            SUBSCRIBE
          </button>
        </Link>

        {user && user.role > 1 && (
          <div className="header-notif-wrapper" ref={notifRef}>
            <button
              type="button"
              className="notif-bell-btn"
              onClick={() => setShowNotifications((prev) => !prev)}
              aria-label="Notifications"
            >
              <FontAwesomeIcon icon={faBell} className="notif-bell-icon" />
              {unreadCount > 0 && (
                <span className="notif-badge">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div className="notif-dropdown">
                <div className="notif-dropdown-header">Notifications</div>

                {notifications.length === 0 ? (
                  <div className="notif-empty">You’re all caught up.</div>
                ) : (
                  <ul className="notif-list">
                    {notifications.map((n) => {
                      const d = n.createdAt ? new Date(n.createdAt) : null;
                      const timestamp = d
                        ? d.toLocaleString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                          })
                        : "";

                      return (
                        <li
                          key={n._id}
                          className="notif-item"
                          onClick={() => openNotification(n)} // click = open draft + mark read
                        >
                          {/* Row 1: title + dismiss button */}
                          <div className="notif-row-top">
                            <span className="notif-title-text">
                              {n.draftTitle || "Untitled draft"}
                            </span>

                            <button
                              type="button"
                              className="notif-dismiss-btn"
                              onClick={(e) => {
                                e.stopPropagation(); // don't trigger openNotification
                                dismissNotification(n._id);
                              }}
                              aria-label="Dismiss notification"
                            >
                              ×
                            </button>
                          </div>

                          {/* Row 2: what happened */}
                          <div className="notif-row-bottom">
                            <span className="notif-body-text">
                              {renderNotificationBody(n)}
                            </span>
                          </div>

                          {/* Row 3: timestamp */}
                          {timestamp && (
                            <div className="notif-time-text">
                              {timestamp}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  )}
                </div>
              )}
            </div>
          )}
        <div className="search-icon" onClick={() => setSearchOpen(true)}>
          <div className="mr-4 inline-block cursor-pointer p-2 text-xl text-dark transition hover:text-primary md:mr-0">
            <FontAwesomeIcon icon={faSearch} size="2x" />
          </div>
        </div>
        <div className="hamburger-icon">
          <input
            id="menu__toggle"
            type="checkbox"
            checked={isMenuOpen}
            onChange={() => setIsMenuOpen(!isMenuOpen)}
          />
          <label className="menu__btn" htmlFor="menu__toggle">
            <FontAwesomeIcon
              icon={faBars}
              size="2x"
              style={{ width: "50px", height: "50px", cursor: "pointer" }}
            />
          </label>
          <div className="menu__overlay" onClick={handleLinkClick}></div>
          <ul className="menu__box" ref={menuRef}>
            <div style={{ marginTop: "115px" }}>
              <li className="menu__item">
                <Link to="/" onClick={handleLinkClick}>
                  Home
                </Link>
              </li>
              {user?.role > 1 && (
                <>
                  <li className="menu__item">
                    <Link to="/write" onClick={closeMenu}>
                      The Writer's Room
                    </Link>
                  </li>
                  <li className="menu__item">
                    <Link to="/edit" onClick={closeMenu}>
                      Editorial Dashboard
                    </Link>
                  </li>
                  <li className="menu__item">
                    <Link to="/referrals" onClick={closeMenu}>
                      Referral Dashboard
                    </Link>
                  </li>
                 <li className="menu__item">
                    <Link to="/affiliates" onClick={closeMenu}>
                      Affiliate Dashboard
                    </Link>
                  </li>
                </>
              )}
              <li className="menu__item">
                <Link to="/articles" onClick={handleLinkClick}>
                  Read Our Articles
                </Link>
              </li>
              <li className="menu__item">
                <Link to="/authors" onClick={handleLinkClick}>
                  Meet Our Writers
                </Link>
              </li>
              <li className="menu__item">
                <Link to="/categories" onClick={handleLinkClick}>
                  Choose Your Genre
                </Link>
              </li>
              <li className="menu__item">
                <Link to="/profile" onClick={handleLinkClick}>
                  Manage Your Account
                </Link>
              </li>
              {user?.role > 4 && (
                <li className="menu__item">
                  <Link to="/admin" onClick={closeMenu}>
                    Admin Dashboard
                  </Link>
                </li>
              )}
              <li className="menu__item">
                {user ? (
                  <a href="#" onClick={handleLogout} className="link-style">Logout</a>
                ) : (
                  <Link to="/login" className="link-style" onClick={handleLinkClick}>Sign In</Link>
                )}
              </li>
            </div>
          </ul>
          <label htmlFor="menu__toggle" className="close__btn">
            <FontAwesomeIcon icon={faCircleXmark} size="2x" />
          </label>
        </div>
      </div>
      {searchOpen && <SearchBar onClose={() => setSearchOpen(false)} />}
      <div className="main-content">
        <div className="content-blur-target">{/* Main content goes here */}</div>
      </div>
    </div>
  );
}

function SearchBar({ onClose }) {
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const searchBarRef = useRef(null);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      onClose();
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchBarRef.current && !searchBarRef.current.contains(e.target)) {
        onClose();
      }
    };

    const handleEscapePress = (e) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscapePress);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapePress);
    };
  }, []);

  const handleClearInput = () => {
    setSearchQuery("");
  };

  return (
    <div className="search-overlay">
      <div className="search-bar-modal" ref={searchBarRef}>
        <button className="search-bar-close" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <form className="search-bar-form" onSubmit={handleSearchSubmit}>
          <input
            type="text"
            placeholder="Search our library..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-bar-input"
            autoFocus
          />
          <button type="submit" className="search-bar-icon">
            <FontAwesomeIcon icon={faSearch} />
          </button>
        </form>
      </div>
    </div>
  );
}
